/**
 * DecisionsSummary.js — Montags-Cockpit
 * Alle Notizen der gewählten Kampagne(n), scrollbar, Status direkt änderbar
 * v1.0 | 2026-03-06
 */

import React, { useState, useEffect, useCallback } from 'react';
import './DecisionsSummary.css';

const API = '/api/ads_decisions.php';

const STATUS_LIST = [
    { key: 'offen',      label: 'O', title: 'Offen',   cls: 'ds-status-offen' },
    { key: 'in_arbeit',  label: 'I', title: 'In Arbeit',  cls: 'ds-status-in_arbeit' },
    { key: 'beobachten', label: 'B', title: 'Beobachten',   cls: 'ds-status-beobachten' },
    { key: 'pausiert',   label: 'P', title: 'Pausiert',   cls: 'ds-status-pausiert' },
    { key: 'umgesetzt',  label: 'U', title: 'Umgesetzt',  cls: 'ds-status-umgesetzt' },
    { key: 'geprueft',   label: 'G', title: 'Geprüft', cls: 'ds-status-geprueft' },
    { key: 'archiv',     label: 'A', title: 'Archiv',  cls: 'ds-status-archiv' },
];
const STATUS_LABELS = Object.fromEntries(STATUS_LIST.map(s => [s.key, s]));

const BEREICH_LABELS = {
    campaigns:       '📊 Kampagne',
    devices:         '📱 Geräte',
    schedule:        '🕐 Werbezeitplan',
    age:             '👥 Alter',
    keywords:        '🔑 Keywords',
    assets:          '🖼️ Assets',
    assets_titles:   '🖼️ Titel',
    assets_texts:    '🖼️ Texte',
    assets_sitelinks:'🖼️ Sitelinks',
    journey:         '👣 Journey',
    auktionen:       '⚡ Auktionen',
    standorte:       '📍 Standorte',
    zielgruppen:     '🎯 Zielgruppen',
    suchbegriffe:    '🔍 Suchbegriffe',
};

export default function DecisionsSummary({ selectedIds, campaignList }) {
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [filter, setFilter]     = useState('offen');   // offen | umgesetzt | geprueft | alle
    const [updating, setUpdating] = useState(null);
    const [editWirkung, setEditWirkung] = useState({});  // { id: text }
    const [editFields, setEditFields]   = useState({});  // { 'id_beob': text, 'id_mass': text }
    const token = localStorage.getItem('token') || '';

    const load = useCallback(async () => {
        if (!selectedIds || selectedIds.length === 0) { setData([]); return; }
        setLoading(true);
        try {
            const url = `${API}?campaign_ids=${selectedIds.join(',')}&token=${token}`;
            const res = await fetch(url);
            const json = await res.json();
            if (json.success) setData(json.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [selectedIds, token]);

    useEffect(() => { load(); }, [load]);

    async function saveField(item, field, value) {
        try {
            const res  = await fetch(API, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ id: item.id, [field]: value }),
            });
            const json = await res.json();
            if (json.success) {
                setData(prev => prev.map(d => d.id === item.id ? { ...d, [field]: value } : d));
            } else {
                console.error('saveField fehlgeschlagen:', field, json);
                alert('Feld "' + field + '" konnte nicht gespeichert werden.');
            }
        } catch (e) { console.error('saveField Fehler:', e); }
    }

    function getField(item, field) {
        const key = item.id + '_' + field;
        return editFields[key] !== undefined ? editFields[key] : (item[field] || '');
    }

    function setField(item, field, value) {
        setEditFields(prev => ({ ...prev, [item.id + '_' + field]: value }));
    }

    async function saveWirkung(item) {
        const wirkung = editWirkung[item.id] ?? item.wirkung ?? '';
        setUpdating(item.id);
        try {
            const res  = await fetch(API, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ id: item.id, wirkung }),
            });
            const json = await res.json();
            if (json.success) {
                setData(prev => prev.map(d => d.id === item.id ? { ...d, wirkung } : d));
            } else {
                console.error('saveWirkung fehlgeschlagen:', json);
                alert('Wirkung konnte nicht gespeichert werden.');
            }
        } catch (e) { console.error(e); }
        setUpdating(null);
    }

    async function setStatus(item, newStatus) {
        setUpdating(item.id);
        try {
            const res  = await fetch(API, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ id: item.id, status: newStatus }),
            });
            const json = await res.json();
            if (json.success) {
                setData(prev => prev.map(d => d.id === item.id ? { ...d, status: newStatus } : d));
                // editFields für dieses Item bereinigen
                setEditFields(prev => {
                    const next = { ...prev };
                    Object.keys(next).filter(k => k.startsWith(item.id + '_')).forEach(k => delete next[k]);
                    return next;
                });
            } else {
                console.error('setStatus fehlgeschlagen:', json);
                alert('Status konnte nicht gespeichert werden. Bitte Seite neu laden.');
            }
        } catch (e) { console.error(e); }
        setUpdating(null);
    }

    // Filtern
    const filtered = filter === 'alle' ? data : data.filter(d => d.status === filter);

    // Nach Bereich gruppieren
    const grouped = {};
    filtered.forEach(d => {
        const b = d.bereich || 'sonstige';
        if (!grouped[b]) grouped[b] = [];
        grouped[b].push(d);
    });



    // Kampagnenname für Anzeige
    // Zähler-Map für Filter-Buttons
    const cntMap = {
        offen:      data.filter(d => d.status === 'offen').length,
        in_arbeit:  data.filter(d => d.status === 'in_arbeit').length,
        beobachten: data.filter(d => d.status === 'beobachten').length,
        pausiert:   data.filter(d => d.status === 'pausiert').length,
        umgesetzt:  data.filter(d => d.status === 'umgesetzt').length,
        geprueft:   data.filter(d => d.status === 'geprueft').length,
        archiv:     data.filter(d => d.status === 'archiv').length,
        alle:       data.length,
    };

    const campNames = selectedIds.map(id => {
        const c = campaignList.find(x => String(x.campaign_id) === String(id));
        return c ? c.campaign_name : id;
    }).join(', ');

    return (
        <div className="ds-wrap">
            {/* Header */}
            <div className="ds-header">
                <div className="ds-title-row">
                    <span className="ds-title">📋 Aktionsplan</span>
                    <span className="ds-campaign">{campNames}</span>
                </div>
                <div className="ds-meta-row">

                    <div className="ds-filter-btns">
                        {[...STATUS_LIST.map(s => s.key), 'alle'].map(f => (
                            <button
                                key={f}
                                className={"ds-filter-btn" + (filter === f ? ' active' : '')}
                                onClick={() => setFilter(f)}
                            >
                                {(f === 'alle' ? 'Alle' : STATUS_LABELS[f]?.label) + ' (' + (cntMap[f] ?? 0) + ')'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Scrollfenster */}
            <div className="ds-scroll">
                {loading && <p className="ds-empty">Lädt…</p>}
                {!loading && filtered.length === 0 && (
                    <p className="ds-empty">
                        {filter === 'offen' ? '✅ Alle Punkte erledigt.' : 'Keine Einträge.'}
                    </p>
                )}
                {!loading && Object.entries(grouped).map(([bereich, items]) => (
                    <div key={bereich} className="ds-group">
                        <div className="ds-group-label">
                            {BEREICH_LABELS[bereich] || bereich}
                            <span className="ds-group-count">{items.length}</span>
                        </div>
                        {items.map(item => (
                            <div key={item.id} className={"ds-item ds-item-" + item.status}>
                                <div className="ds-item-body">
                                    <textarea
                                        className="ds-edit-input ds-edit-beob"
                                        rows={2}
                                        value={getField(item, 'beobachtung')}
                                        onChange={e => setField(item, 'beobachtung', e.target.value)}
                                        onBlur={e => saveField(item, 'beobachtung', e.target.value)}
                                    />
                                    <textarea
                                        className="ds-edit-input ds-edit-mass"
                                        placeholder="Maßnahme…"
                                        rows={2}
                                        value={getField(item, 'massnahme')}
                                        onChange={e => setField(item, 'massnahme', e.target.value)}
                                        onBlur={e => saveField(item, 'massnahme', e.target.value)}
                                    />
                                    {(item.status !== 'offen') && (
                                        <div className="ds-wirkung-row">
                                            <textarea
                                                className="ds-wirkung-input"
                                                placeholder="Wirkung nach Umsetzung…"
                                                rows={2}
                                                value={editWirkung[item.id] !== undefined ? editWirkung[item.id] : (item.wirkung || '')}
                                                onChange={e => setEditWirkung(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                onBlur={() => saveWirkung(item)}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="ds-item-right">
                                    <div className="ds-status-options">
                                        {STATUS_LIST.map(s => (
                                            <button
                                                key={s.key}
                                                className={"ds-status-opt " + s.cls + (item.status === s.key ? ' active' : '')}
                                                onClick={() => setStatus(item, s.key)}
                                                disabled={updating === item.id}
                                                title={s.title}
                                            >
                                                {updating === item.id && item.status !== s.key ? '' : s.label}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="date"
                                        className="ds-datum-input"
                                        value={getField(item, 'datum')}
                                        onChange={e => setField(item, 'datum', e.target.value)}
                                        onBlur={e => saveField(item, 'datum', e.target.value)}
                                        title="Datum ändern"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
}